import { differenceInCalendarDays } from 'date-fns'

import type { ExerciseMuscle, MuscleLoad, MuscleSlug } from '@/api/types/workout'
import { parseUtcDate } from '@/lib/date-utils'

/** 体の上から順 */
export const MUSCLE_SLUGS: readonly MuscleSlug[] = [
  'chest',
  'deltoids',
  'biceps',
  'triceps',
  'forearm',
  'abs',
  'obliques',
  'neck',
  'trapezius',
  'upper-back',
  'lower-back',
  'gluteal',
  'quadriceps',
  'hamstring',
  'adductors',
  'calves',
  'tibialis',
]

export const MUSCLE_LABELS: ReadonlyMap<MuscleSlug, string> = new Map<MuscleSlug, string>([
  ['chest', '胸'],
  ['deltoids', '肩'],
  ['biceps', '二頭'],
  ['triceps', '三頭'],
  ['forearm', '前腕'],
  ['abs', '腹直筋'],
  ['obliques', '腹斜筋'],
  ['neck', '首'],
  ['trapezius', '僧帽筋'],
  ['upper-back', '上背部'],
  ['lower-back', '腰'],
  ['gluteal', '臀部'],
  ['quadriceps', '大腿四頭'],
  ['hamstring', 'ハム'],
  ['adductors', '内転筋'],
  ['calves', 'ふくらはぎ'],
  ['tibialis', 'すね'],
])

export const muscleLabel = (slug: MuscleSlug): string => MUSCLE_LABELS.get(slug) ?? slug

export const isMuscleSlug = (value: string): value is MuscleSlug => MUSCLE_SLUGS.some((s) => s === value)

export type MuscleLevel = 0 | 1 | 2 | 3 | 4

/** 段階ごとの色。0 は未実施（人体図の地の色を使う） */
export const MUSCLE_LEVEL_COLORS: readonly string[] = ['', '#fca5a5', '#f87171', '#dc2626', '#991b1b']

/** 主働筋・補助筋の色（種目の筋群表示用） */
export const MUSCLE_ROLE_COLORS: ReadonlyMap<ExerciseMuscle['role'], string> = new Map([
  ['primary', '#dc2626'],
  ['secondary', '#fca5a5'],
])

/**
 * 実効セット数を週あたりに換算して 4 段階に分ける。
 * 1 部位あたり週 10 セット前後が筋肥大の目安なので、10 以上を最も濃くする。
 */
export const muscleLevel = (effectiveSets: number, days: number): MuscleLevel => {
  if (effectiveSets <= 0) return 0
  const weekly = (effectiveSets * 7) / days
  if (weekly < 3) return 1
  if (weekly < 6) return 2
  if (weekly < 10) return 3
  return 4
}

/** タップした筋群の役割を なし → 主 → 補助 → なし と切り替える */
export const cycleMuscleRole = (muscles: ExerciseMuscle[], slug: MuscleSlug): ExerciseMuscle[] => {
  const current = muscles.find((m) => m.muscle === slug)
  if (!current) return [...muscles, { muscle: slug, role: 'primary' }]
  if (current.role === 'primary') {
    return muscles.map((m) => (m.muscle === slug ? { muscle: slug, role: 'secondary' } : m))
  }
  return muscles.filter((m) => m.muscle !== slug)
}

/** 最後に鍛えた日を「今日 / 昨日 / N日前」で返す。未実施なら null */
export const trainedAgoLabel = (iso: string | null, now: Date = new Date()): string | null => {
  if (!iso) return null
  const days = differenceInCalendarDays(now, parseUtcDate(iso))
  if (days <= 0) return '今日'
  if (days === 1) return '昨日'
  return `${days}日前`
}

/** 鍛えた筋群（API の並び）のあとに、まだ鍛えていない筋群を体の上から順に 0 で補う */
export const withAllMuscles = (loads: MuscleLoad[]): MuscleLoad[] => {
  const trained = new Set(loads.map((l) => l.muscle))
  const rest = MUSCLE_SLUGS.filter((s) => !trained.has(s)).map(
    (muscle): MuscleLoad => ({
      muscle,
      effective_sets: 0,
      primary_sets: 0,
      secondary_sets: 0,
      last_trained_at: null,
    }),
  )
  return [...loads, ...rest]
}
